import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useServerContext, useOffers, useUserData } from "src/hooks/redux";
import { Offer } from "src/utils/redux/slices/serverContext";
import { shuffleArray } from "src/utils/arrayMethods";
import {
  updateShippingInfoError,
  updateShippingInfo,
  updateShippingStatus,
  updatePurchasedPlans,
} from "src/utils/redux/slices/offers";

export const getSingleInAppOffers = (serverContext, noomPremiumPurchased) => {
  const { offers } = serverContext;
  if (noomPremiumPurchased) {
    return offers["hw-addons-baseline"].single || [];
  }
  return offers["hw-in_app_offers-baseline"]?.single || [];
};

const getShuffledInAppStoreOffers = (serverContext, noomPremiumPurchased) => {
  const { offers } = serverContext;
  if (noomPremiumPurchased) {
    return shuffleArray(offers["hw-baseline-addons-store"]?.store) || [];
  }
  return offers["hw-in_app_offers-baseline"]?.store || [];
};

export const useInAppOffersContext = () => {
  const serverContext = useServerContext();
  const userData = useUserData();

  const [inAppOffers, setInAppOffers] = useState<Offer[]>([]);
  const noomPremiumPurchased = userData.NoomPremiumSurveyCompleteDateISO;

  const singleInAppOffers = getSingleInAppOffers(
    serverContext,
    noomPremiumPurchased
  );

  useEffect(() => {
    const shuffledInAppStoreOffers = getShuffledInAppStoreOffers(
      serverContext,
      noomPremiumPurchased
    );
    setInAppOffers(shuffledInAppStoreOffers);
  }, [serverContext, noomPremiumPurchased]);

  const offers = useOffers();
  const dispatch = useDispatch();

  const setShippingInfo = useCallback(
    (shippingInfo) => {
      dispatch(updateShippingInfo(shippingInfo));
    },
    [dispatch]
  );

  const setShippingInfoError = (shippingInfoError) => {
    dispatch(updateShippingInfoError(shippingInfoError));
  };

  const setShippingStatus = (shippingStatus) => {
    dispatch(updateShippingStatus(shippingStatus));
  };

  const setPurchasedPlans = (purchasedPlans) => {
    dispatch(updatePurchasedPlans(purchasedPlans));
  };

  const { shippingInfo, shippingStatus, shippingInfoError, purchasedPlans } =
    offers;

  const name = userData?.name;
  useEffect(() => {
    if (name) {
      const nameSplit = name.split(" ");
      const firstName = nameSplit[0];
      const lastName = nameSplit.slice(1).join(" ");

      setShippingInfo({
        ...shippingInfo,
        firstName,
        lastName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, setShippingInfo]);

  return {
    inAppOffers,
    singleInAppOffers,
    shippingInfo,
    setShippingInfo,
    shippingInfoError,
    setShippingInfoError,
    shippingStatus,
    setShippingStatus,
    setPurchasedPlans,
    purchasedPlans,
  };
};
